import {
  AttributeCategory,
  AttributeType,
  CampaignCreator,
  TwinCampaign,
  SystemOnboarding,
  TwinForm,
  TwinCampaignProduct,
} from 'constants/types'

import {
  ProfileAttribute,
  CatalogItem,
  twinStatusesCodes,
  twinStatusesMap,
} from 'shared'

import * as yup from 'yup'
import moment from 'moment/moment'
import { ENABLE_TIERS } from 'constants/config'

export const getCatalogOptions = (data: CatalogItem[] | undefined) => {
  if (!data) return []
  return data.map((el) => ({
    label: el.text,
    value: el.code,
  }))
}

export const filenameSchema = yup
  .string()
  .matches(
    /^[\w\-._]+$/,
    'Invalid file name. Cannot contain < > : " / \\ | ? * characters.'
  )
  .min(1, 'File name cannot be empty')
  .max(255, 'File name is too long')

export const getAttributeValue = (
  attributes: ProfileAttribute[],
  attributeType: AttributeType,
  category?: AttributeCategory
) => {
  const activeAttribute = attributes
    .filter(({ category: itemCategory }) =>
      category ? itemCategory === category : true
    )
    .find(({ type: itemAttribute }) => itemAttribute === attributeType)
    ?.value as CatalogItem

  if (!activeAttribute) return null

  if (typeof activeAttribute === 'string') {
    return {
      value: attributeType,
      label: activeAttribute,
    }
  }

  return {
    value: activeAttribute.code,
    label: activeAttribute.text,
  }
}

export const getAttributeValues = (
  attributes: ProfileAttribute[],
  attributeType: AttributeType,
  category?: AttributeCategory
) => {
  const activeAttribute = attributes
    .filter(({ category: itemCategory }) =>
      category ? itemCategory === category : true
    )
    .find(({ type }) => type === attributeType)?.value as CatalogItem[]

  if (!activeAttribute?.length) return []

  return activeAttribute.map((el) => ({
    value: el.code,
    label: el.text,
  }))
}

export const getIsUpcomingDeadline = (deadline: string | undefined) => {
  if (!deadline || deadline === 'Not set') return

  const beforeDate = moment(deadline).utc().subtract(3, 'days')

  return moment()
    .utc()
    .isBetween(beforeDate, moment(deadline).utc(), 'days', '[]')
}

export const manageTwinStatus = (
  creator: CampaignCreator | undefined,
  campaign: TwinCampaign | undefined
) => {
  const submissionForms = campaign?.forms.filter(
    (form) =>
      form.type === 'SUBMISSION_FORM' &&
      form.campaignCreatorId === creator?.creatorId
  )

  if (submissionForms?.length) {
    const statusForms: {
      overdue: TwinForm | null
      enrolled: TwinForm | null
      submitted: TwinForm | null
    } = {
      overdue: null,
      enrolled: null,
      submitted: null,
    }
    submissionForms.forEach((form) => {
      switch (form.status.code) {
        case twinStatusesCodes.overdue:
          return (statusForms.overdue = form)
        case twinStatusesCodes.enrolled:
          return (statusForms.enrolled = form)
        case twinStatusesCodes.submitted:
          return (statusForms.submitted = form)
        default:
          return
      }
    })
    return (
      statusForms.overdue?.status ||
      statusForms.enrolled?.status ||
      statusForms.submitted?.status ||
      submissionForms[0]?.status
    )
  }

  return creator?.status
}

export const getAllCreatorSubmissionForms = (
  campaign: TwinCampaign,
  creator: CampaignCreator | undefined
) =>
  campaign?.forms?.filter(
    (form) =>
      form.type === 'SUBMISSION_FORM' &&
      form.campaignCreatorId === creator?.creatorId
  ) || []

export const setAttributeValue = (
  attributes: ProfileAttribute[] | undefined,
  {
    attribute,
    category,
    value,
  }: {
    attribute: AttributeType
    category: AttributeCategory
    value: CatalogItem | CatalogItem[] | string | null
  }
) => {
  //Creates new object references
  const newAttributes = attributes?.map((el) => ({ ...el })) || []

  const itemIdx = newAttributes.findIndex(
    ({ category: itemCategory, type }) => {
      return itemCategory === category && type === attribute
    }
  )
  if (itemIdx === -1) {
    newAttributes.push({
      category,
      type: attribute,
      value: value,
    } as ProfileAttribute)
  } else {
    newAttributes[itemIdx].value = value
  }
  return newAttributes
}

export const formatCreatorStatusText = (statusCode: string) => {
  if (
    statusCode === twinStatusesCodes['application-approved'] ||
    statusCode === twinStatusesCodes['application-rejected']
  ) {
    return twinStatusesMap['application-submitted']
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  //@ts-expect-error
  return twinStatusesMap[statusCode]
}
export const getOnboardingFromStorage = (): SystemOnboarding =>
  JSON.parse(localStorage.getItem('onboarding') || '{}')

export const addUserInfoToStorage = (userId: string) =>
  localStorage.setItem('hue-twin', userId)

export const getUserInfoFromStorage = (): string => {
  const twinInfo = localStorage.getItem('hue-twin')
  return twinInfo || ''
}

export const removeUserInfoFromStorage = () => {
  localStorage.removeItem('hue-twin')
}

export const calculateCampaignMaxReward = (
  campaign: TwinCampaign | undefined
) => {
  if (!campaign || !campaign.products?.length) return 0

  return campaign.products.reduce((totalReward, product) => {
    if (!product.rewards?.length) return totalReward

    const productReward = product.rewards.reduce(
      (sum, reward) => sum + (reward.amount || 0),
      0
    )

    return totalReward + productReward
  }, 0)
}

export const calculateProductMaxReward = (
  product: TwinCampaignProduct | undefined
) => {
  if (!product) return 0
  return product.rewards.reduce((sum, reward) => sum + (reward.amount || 0), 0)
}

export const campaignHasPaidRewards = (
  campaign: TwinCampaign | undefined | null
) => {
  return campaign?.products?.some((product) =>
    product.rewards?.some((reward) => reward.rewardType === 'PAID')
  )
}

export const checkCampaignLocked = (
  creatorTier?: number,
  campaignTier?: number
) => {
  if (!campaignTier || !creatorTier) return false

  return ENABLE_TIERS
    ? Boolean(creatorTier && creatorTier < campaignTier)
    : false
}
